/**
 * This file defines both Features and their corresponding Feature Sets.
 * It also maps Feature Sets to different Feature Packages for Outfitter Groups in HQ.
 * The `featurePackageToFeatures` object provides a lookup for determining which Features are included in each Feature Package.
 */

import { OperatorGroupFeatureFlag } from './operator-group-feature-flags'
import { OutfitterFeaturePackage, OutfitterFeaturePackageOptions } from './operator-groups-model'

export const featureSets = {
  RentalOperations: [
    'RentalAddOns',
    'WaiverContent',
    'CheckOut',
    'CheckIn',
    'PrepVehicle',
    'AdventureListings',
    'ReservableInventory'
  ],
  ReservationsAndPayments: [
    'Reservations',
    'ReservationsCalendar',
    'RetailSales',
    'Affiliates',
    'Promotions',
    'Merchandise',
    'Customizations',
    'TaxesAndFees',
    'Disputes'
  ],
  FleetEssentials: [
    'Vehicles',
    'WorkOrders',
    'Dispositions',
    'ServiceBulletins',
    'AskPolarisCases',
    'VehicleOrders',
    'VehicleDocuments',
    'ServiceReminders',
    'MaintenancePrograms'
  ],
  AdvancedFleetManagement: ['OtherRentalAssets', 'OutfitterOwnedVehicles'],
  AdminTools: ['OutfitterDetails', 'ManageTeam', 'PaymentInformation', 'FactoryDirectAdvantage', 'FieldGuide'],
  Reporting: ['ReportGallery', 'Customers'],
  TrainingAndSupport: ['MpwrLearn', 'QuickLinks', 'Claims', 'OutfitterSummit'],
  HardwareAndIntegrations: ['CardReaders', 'MessageTemplates']
} as const satisfies Readonly<{ [key: string]: string[] }>

export type FeatureSet = keyof typeof featureSets
export type OutfitterFeature = (typeof featureSets)[FeatureSet][number]

export const featurePackageToFeatureSets = {
  Arrival: ['AdminTools', 'FleetEssentials', 'TrainingAndSupport', 'Reporting', 'RentalOperations'],
  MPWRBook: [
    'AdminTools',
    'FleetEssentials',
    'RentalOperations',
    'ReservationsAndPayments',
    'AdvancedFleetManagement',
    'HardwareAndIntegrations',
    'TrainingAndSupport',
    'Reporting'
  ],
  DealerDemoRide: ['AdminTools', 'FleetEssentials', 'TrainingAndSupport', 'Reporting', 'RentalOperations']
} satisfies { [key in OutfitterFeaturePackage]: FeatureSet[] }

export const featurePackageToFeatures = Object.fromEntries(
  OutfitterFeaturePackageOptions.map((featurePackage) => {
    const featureSetsInPackage: FeatureSet[] = featurePackageToFeatureSets[featurePackage]
    const features: OutfitterFeature[] = featureSetsInPackage.flatMap((featureSet) => {
      const featuresForSet = featureSets[featureSet]
      return featuresForSet
    })

    return [featurePackage, features] as const
  })
) as { [key in OutfitterFeaturePackage]: OutfitterFeature[] }

export type ExperimentalOutfitterFeature = OperatorGroupFeatureFlag
export type CombinedFeature = OutfitterFeature | ExperimentalOutfitterFeature

// TYPE TEST - DO NOT REMOVE
type TypeEqual<Target, Value> = (<T>() => T extends Target ? 1 : 2) extends <T>() => T extends Value ? 1 : 2 ? true : false
type Expect<T extends true> = T
type NoFeatureFlagOverlapWithFeatures = Expect<TypeEqual<ExperimentalOutfitterFeature & OutfitterFeature, never>>
